import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { q } from '../../api/queries';
import { useNames } from '../../api/names';
import { ErrorNotice, Verdict, type Outcome } from '@bounded/ui/state';
import { Empty, Panel, Select, Skeleton, Table, col } from '@bounded/ui/primitives';
import { useNow } from '@bounded/ui/time';
import { useFormat, useT } from '../../i18n';

export const Route = createFileRoute('/_app/projects/$projectId/decisions/')({
  validateSearch: (s): { outcome?: Outcome } => ({ outcome: s.outcome === 'ALLOW' || s.outcome === 'ASK' || s.outcome === 'DENY' ? s.outcome : undefined }),
  component: Decisions,
});

function Decisions() {
  const t = useT(); const m = t.decisions; const f = useFormat();
  const { projectId } = Route.useParams();
  const { outcome } = Route.useSearch();
  const navigate = Route.useNavigate();
  const decisions = useQuery(q.decisions(projectId));
  const names = useNames();
  const now = useNow(10_000);
  const rows = (decisions.data?.items ?? []).filter((d) => !outcome || d.outcome === outcome);

  return (
    <Panel
      title={m.title}
      description={m.description}
      footer={decisions.data && <p className="text-sm text-graphite">{m.incomplete}</p>}
      actions={
         <Select aria-label={m.filter} className="w-full sm:w-40" value={outcome ?? ''} onChange={(e) => void navigate({ search: { outcome: (e.target.value || undefined) as Outcome | undefined } })}>
          <option value="">{m.all}</option><option value="ALLOW">{m.allowed}</option><option value="ASK">{m.asked}</option><option value="DENY">{m.denied}</option>
        </Select>
      }>
      {decisions.isPending && <div className="p-4"><Skeleton className="h-40" /></div>}
      {decisions.error && <div className="p-4"><ErrorNotice error={decisions.error} onRetry={() => void decisions.refetch()} /></div>}
      {decisions.data && rows.length === 0 && <Empty title={m.emptyTitle}>{m.emptyBody}</Empty>}
      {rows.length > 0 && (
        <Table head={[{ label: m.cols.verdict, className: 'w-28' }, { label: m.cols.why }, { label: m.cols.device, className: `w-44 ${col.hideMd}` }, { label: m.cols.when, className: 'w-36 text-right' }]} minWidth="0">
          {rows.map((d) => (
            <tr key={`${d.device_id}/${d.evaluation_id}`}>
              <td><Verdict outcome={d.outcome} /></td>
              {/* Known reason codes are explained; unknown ones are shown verbatim rather than guessed at. */}
              <td>{d.reason_codes.map((r) => m.reasons[r] ?? r).join('; ')}
                {d.has_detail && <Link to="/projects/$projectId/decisions/$deviceId/$evaluationId" params={{ projectId, deviceId: d.device_id, evaluationId: d.evaluation_id }} className="ml-2 text-sm text-boundary hover:underline">{t.audit.open}</Link>}</td>
              <td className={col.hideMd}><Link to="/projects/$projectId/devices/$deviceId" params={{ projectId, deviceId: d.device_id }} className="hover:underline">{names.device(d.device_id)}</Link></td>
              <td className="text-right text-graphite" title={f.dateTime(d.decided_at)}>{f.ago(d.decided_at, now)}</td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}
